import { Fragment, useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import {
  claudeSignInResultSchema,
  type ClaudeSignInResult,
  type SetupStepAction,
  type SetupStepId,
  type SetupStepStatus,
  type ToolboxBridge,
} from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { SETUP_STEP_TITLES, stepNumber, type SetupRow, type SetupRows } from "@/lib/toolbox/setup";

/** The note about Claude's sign-in (v2 desktop design, first launch), word for word. Shown under step 3 when it offers sign-in. */
export const CLAUDE_SIGN_IN_NOTE =
  "\"Sign in to Claude\" opens Claude Code's own sign-in in your browser. You sign in with your Toro Claude Team account. This app never sees or stores your Claude password or token.";

/** The button label when the app sends none. */
const ACTION_LABELS: Readonly<Record<SetupStepAction, string>> = {
  "install-claude-code": "Install Claude Code",
  "sign-in-to-claude": "Sign in to Claude",
  "install-git": "Install Git",
  "get-project-code": "Get the code",
  retry: "Try again",
  "install-github-cli": "Install the GitHub CLI",
  "sign-in-to-github": "Sign in to GitHub",
};

/** What a `running` step's note says (frame 1b), one sentence per step. */
const RUNNING_NOTES: Partial<Record<SetupStepId, string>> = {
  "claude-code": "If this computer didn't have Claude Code, the Toolbox installs it for you. No admin rights needed.",
  git: "If this computer didn't have Git, the Toolbox installs it for you. No admin rights needed.",
  "project-code": "The Toolbox copies your project's code so Claude can read it when it writes a brief.",
};

/** A step's line when it passes and the app sent no detail. */
const OK_DETAILS: Readonly<Record<SetupStepId, string>> = {
  spectree: "Signed in",
  "azure-devops": "Works: token saved and tested",
  "claude-code": "Ready",
  git: "Ready",
  "project-code": "Ready",
};

/** The circle's look for each state (frame 1b `.sn`). Text greys stay at TOOLBOX_TEXT.faint or lighter. */
const CIRCLE_CLASSES: Readonly<Record<SetupStepStatus, string>> = {
  ok: "bg-[rgba(34,197,94,.16)] text-[#4ADE80]",
  running: "border-2 border-[#23A4FF] border-r-transparent text-[#6CC4FF] animate-spin",
  todo: "border border-[#52525B] text-[#D4D4D8]",
  waiting: cn("border border-dashed border-[#3F3F46]", TOOLBOX_TEXT.faint),
  "needs-role": cn("border border-dashed border-[#3F3F46]", TOOLBOX_TEXT.faint),
  failed: "bg-[rgba(225,24,55,.16)] text-[#FF6B81]",
};

const STATUS_LABELS: Readonly<Record<SetupStepStatus, string>> = {
  ok: "Done",
  running: "In progress",
  todo: "To do",
  waiting: "Waiting",
  "needs-role": "Needs a role",
  failed: "Needs attention",
};

function StepCircle({ status, number }: { status: SetupStepStatus; number: number }) {
  return (
    <span className="relative flex h-[26px] w-[26px] items-center justify-center" aria-label={`Step ${String(number)}: ${STATUS_LABELS[status]}`}>
      {status === "running" ? (
        // The ring spins; the number stays still in its own layer.
        <span className={cn("absolute inset-0 box-border rounded-full", CIRCLE_CLASSES.running)} aria-hidden="true" />
      ) : null}
      <span
        className={cn(
          "relative box-border flex h-[26px] w-[26px] items-center justify-center rounded-full text-xs font-semibold",
          status === "running" ? "text-[#6CC4FF]" : CIRCLE_CLASSES[status],
        )}
        aria-hidden="true"
      >
        {status === "ok" ? <Check className="h-3.5 w-3.5" /> : status === "failed" ? <X className="h-3.5 w-3.5" /> : number}
      </span>
    </span>
  );
}

export interface StepRowProps {
  id: SetupStepId;
  status: SetupStepStatus;
  /** The line beside the title. */
  detail: ReactNode;
  /** The one button, or a ghost "Change". */
  action?: ReactNode;
  /** Shown under the detail (progress, results, notes). */
  below?: ReactNode;
  /** A panel across the row's last three columns (frame 1c `.expand`). */
  expand?: ReactNode;
}

/** One row of the Setup list (frame 1b `.step`): circle | title | detail | button. */
export function StepRow({ id, status, detail, action, below, expand }: StepRowProps) {
  const number = stepNumber(id);
  const dimmed = status === "waiting" || status === "needs-role";
  return (
    <li
      className={cn(
        "grid grid-cols-[30px_150px_minmax(0,1fr)_auto] gap-x-3.5 border-t border-[#1F1F23] px-[18px] py-[15px] first:border-t-0",
        expand ? "items-start" : "items-center",
      )}
      data-testid={`setup-step-${id}`}
      data-status={status}
    >
      <span className={cn(dimmed && "opacity-55")}>
        <StepCircle status={status} number={number} />
      </span>
      <span className={cn("font-display text-sm font-semibold", expand && "pt-1", dimmed && "opacity-55")}>
        {SETUP_STEP_TITLES[id]}
      </span>
      <div className={cn("flex min-w-0 flex-col", expand && "pt-1", dimmed && "opacity-55")}>
        <span className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid={`setup-step-${id}-detail`}>
          {detail}
        </span>
        {below}
      </div>
      <div className="flex justify-end">{action}</div>
      {expand ? <div className="col-start-2 col-end-5 mt-3">{expand}</div> : null}
    </li>
  );
}

/** The sign-in result line: "Signed in · Premium seat", or why it failed. Pure; exported for tests. */
export function signInResultLine(result: ClaudeSignInResult): string {
  if (!result.ok) return result.reason ?? "Claude Code did not finish signing in. Try again.";
  if (!result.seat) return "Signed in";
  const seat = /\bseat$/i.test(result.seat) ? result.seat : `${result.seat} seat`;
  return `Signed in · ${seat}`;
}

interface StepOutcome {
  ok: boolean;
  text: string;
}

export interface SetupStepListProps {
  bridge: ToolboxBridge;
  rows: SetupRows;
  /** Step 2, drawn by the Connect Azure DevOps step (it owns its own row). */
  azureDevOps: ReactNode;
  /** The person's name for step 1 ("Signed in as …"). */
  signedInAs?: string;
  /** A report the app returned from `runSetupStep`; the caller parses it. */
  onReport: (value: unknown) => boolean;
  /** Re-read the report (after a sign-in, or when the app returned nothing usable). */
  onRecheck: () => void;
}

function canRun(bridge: ToolboxBridge, action: SetupStepAction): boolean {
  if (action === "sign-in-to-claude") return true;
  return typeof bridge.app.runSetupStep === "function";
}

function detailOf(step: SetupRow, signedInAs: string | undefined): string {
  if (step.status === "waiting") {
    const previous = stepNumber(step.id) - 1;
    return `Waits for step ${String(step.waitsFor ? stepNumber(step.waitsFor) : previous)}`;
  }
  if (step.status === "needs-role") return step.detail ?? "Needs a role on a project first";
  if (step.status === "ok") {
    if (step.id === "spectree" && signedInAs) return `Signed in as ${signedInAs}`;
    return step.detail ?? OK_DETAILS[step.id];
  }
  if (step.status === "running") return step.detail ?? "Working…";
  return step.detail ?? (step.status === "failed" ? "This step didn't finish. Try again." : "Not done yet");
}

/**
 * The Setup steps as one ordered list (frame 1b; two for a Viewer). Each failing row has
 * at most one button that does the fix: `runSetupStep(action)`, or for
 * Claude's sign-in `signInToClaude()` (older apps: `openClaudeSignIn()`),
 * with the result shown on the row. Never a command.
 */
export function SetupStepList({ bridge, rows, azureDevOps, signedInAs, onReport, onRecheck }: SetupStepListProps) {
  const [busy, setBusy] = useState<SetupStepId | null>(null);
  const [outcomes, setOutcomes] = useState<Partial<Record<SetupStepId, StepOutcome>>>({});

  const run = async (step: SetupRow, action: SetupStepAction) => {
    setBusy(step.id);
    setOutcomes((current) => ({ ...current, [step.id]: undefined }));
    try {
      if (action === "sign-in-to-claude") {
        if (typeof bridge.app.signInToClaude === "function") {
          const parsed = claudeSignInResultSchema.safeParse(await bridge.app.signInToClaude());
          const result: ClaudeSignInResult = parsed.success ? parsed.data : { ok: false };
          setOutcomes((current) => ({ ...current, [step.id]: { ok: result.ok, text: signInResultLine(result) } }));
        } else {
          await bridge.app.openClaudeSignIn();
        }
        onRecheck();
        return;
      }
      if (typeof bridge.app.runSetupStep !== "function") return;
      const report = await bridge.app.runSetupStep(action);
      if (!onReport(report)) onRecheck();
    } catch {
      setOutcomes((current) => ({
        ...current,
        [step.id]: { ok: false, text: "That didn't work. Try again in a moment." },
      }));
      onRecheck();
    } finally {
      setBusy(null);
    }
  };

  return (
    <ul aria-label="Setup steps" className="overflow-hidden rounded-xl border border-[#1F1F23] bg-[#0E0E10]">
      {rows.steps.map((step) => {
        if (step.id === "azure-devops") return <Fragment key={step.id}>{azureDevOps}</Fragment>;

        const stepAction = step.action;
        const actionable = (step.status === "todo" || step.status === "failed") && stepAction !== undefined && !step.needsAppUpdate;
        const action =
          actionable && canRun(bridge, stepAction) ? (
            <Button
              size="sm"
              className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
              disabled={busy !== null}
              onClick={() => void run(step, stepAction)}
            >
              {step.actionLabel ?? ACTION_LABELS[stepAction]}
            </Button>
          ) : null;
        const outcome = outcomes[step.id];
        const offersSignIn = actionable && stepAction === "sign-in-to-claude";

        return (
          <StepRow
            key={step.id}
            id={step.id}
            status={step.status}
            detail={
              step.status === "running" && step.progress !== undefined ? (
                <>
                  {detailOf(step, signedInAs)} <span className={TOOLBOX_TEXT.faint}>{Math.round(step.progress)}%</span>
                </>
              ) : (
                detailOf(step, signedInAs)
              )
            }
            action={action}
            below={
              <>
                {step.status === "running" && step.progress !== undefined ? (
                  <span
                    className="mt-[7px] block h-1.5 overflow-hidden rounded-[3px] bg-[#27272A]"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(step.progress)}
                    aria-label={`${SETUP_STEP_TITLES[step.id]} progress`}
                  >
                    <i className="block h-full rounded-[3px] bg-[#23A4FF]" style={{ width: `${String(step.progress)}%` }} />
                  </span>
                ) : null}
                {step.status === "running" && RUNNING_NOTES[step.id] ? (
                  <span className={cn("mt-1.5", TOOLBOX_TEXT.content, TOOLBOX_TEXT.faint)}>{RUNNING_NOTES[step.id]}</span>
                ) : null}
                {outcome ? (
                  <span
                    className={cn("mt-1.5", TOOLBOX_TEXT.content, outcome.ok ? "font-semibold text-[#4ADE80]" : "text-[#FF8A9B]")}
                    data-testid={`setup-step-${step.id}-result`}
                    role="status"
                  >
                    {outcome.text}
                  </span>
                ) : null}
                {offersSignIn ? (
                  <span className={cn("mt-1.5 leading-relaxed", TOOLBOX_TEXT.content, TOOLBOX_TEXT.faint)}>{CLAUDE_SIGN_IN_NOTE}</span>
                ) : null}
              </>
            }
          />
        );
      })}
    </ul>
  );
}
